import React, { ChangeEvent, DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  FileUp,
  FolderOpen,
  Loader2,
  LogOut,
  ShieldAlert,
  UploadCloud,
  X,
  XCircle
} from 'lucide-react';
import nascarLogo from './assets/nascar-productions-logo.png';
import './styles.css';

type SessionUser = {
  sub: string;
  name?: string | null;
  email?: string | null;
  picture?: string | null;
  roles: string[];
};

type MeResponse = {
  authenticated: true;
  authorized: boolean;
  required_role: string;
  csrf_token: string;
  user: SessionUser;
};

type UploadMetadata = {
  job_name: string;
  description: string;
  camera_crew_names?: string;
  location?: string;
  date_of_shoot?: string;
  job_number?: string;
};

type UploadPartUrl = {
  part_number: number;
  url: string;
  expires_in: number;
};

type UploadUrlResponse = {
  upload_type: 'single' | 'multipart';
  method: 'PUT';
  bucket: string;
  object_key: string;
  content_type: string;
  size_bytes: number;
  expires_in: number;
  upload_url?: string | null;
  upload_id?: string | null;
  part_size_bytes?: number | null;
  parts: UploadPartUrl[];
  headers: Record<string, string>;
};

type CompletedUploadPart = {
  part_number: number;
  etag: string;
};

type UploadBatchCompleteRequest = {
  metadata: UploadMetadata;
  file_count: number;
  total_size_bytes: number;
};

type UploadStatus = 'queued' | 'preparing' | 'uploading' | 'finalizing' | 'complete' | 'failed' | 'cancelled';

type UploadItem = {
  id: string;
  file: File;
  relativePath?: string;
  metadata: UploadMetadata;
  status: UploadStatus;
  progress: number;
  uploadedBytes: number;
  speedBps: number;
  etaSeconds: number | null;
  error?: string;
  objectKey?: string;
  controller?: AbortController;
};

type Notice = {
  id: string;
  tone: 'success' | 'error' | 'info';
  message: string;
};

type SelectedFile = {
  file: File;
  relativePath?: string;
};

type FileWithRelativePath = File & {
  webkitRelativePath?: string;
};

type BrowserFileSystemEntry = FileSystemEntry;
type BrowserFileSystemFileEntry = FileSystemFileEntry;
type BrowserFileSystemDirectoryEntry = FileSystemDirectoryEntry;
type BrowserFileSystemDirectoryReader = FileSystemDirectoryReader;

type DataTransferItemWithEntry = DataTransferItem & {
  webkitGetAsEntry?: () => BrowserFileSystemEntry | null;
};

const APP_TITLE = 'NASCAR MEDIA PORTAL UPLOAD';
const initialMetadata: UploadMetadata = {
  job_name: '',
  description: '',
  camera_crew_names: '',
  location: '',
  date_of_shoot: '',
  job_number: ''
};

function App() {
  const [session, setSession] = useState<MeResponse | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [metadata, setMetadata] = useState<UploadMetadata>(initialMetadata);
  const [queue, setQueue] = useState<UploadItem[]>([]);
  const [notices, setNotices] = useState<Notice[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const isUnauthorizedRoute = window.location.pathname === '/unauthorized';

  const addNotice = useCallback((tone: Notice['tone'], message: string) => {
    const id = crypto.randomUUID();
    setNotices((items) => [...items, { id, tone, message }]);
    window.setTimeout(() => {
      setNotices((items) => items.filter((item) => item.id !== id));
    }, 6000);
  }, []);

  const refreshSession = useCallback(async () => {
    setLoadingSession(true);
    try {
      const response = await fetch('/me', { credentials: 'include' });
      if (response.status === 401) {
        setSession(null);
        return;
      }
      if (!response.ok) {
        throw new Error('Unable to load session');
      }
      setSession(await response.json());
    } catch {
      setSession(null);
    } finally {
      setLoadingSession(false);
    }
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    document.title = APP_TITLE;
  }, []);

  const setFolderInputRef = useCallback((node: HTMLInputElement | null) => {
    folderInputRef.current = node;
    if (node) {
      node.setAttribute('webkitdirectory', '');
      node.setAttribute('directory', '');
    }
  }, []);

  const startLogin = async () => {
    const response = await fetch('/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ return_to: '/' })
    });
    if (!response.ok) {
      addNotice('error', 'Login could not be started.');
      return;
    }
    const payload = (await response.json()) as { authorization_url: string };
    window.location.assign(payload.authorization_url);
  };

  const logout = async () => {
    const response = await fetch('/logout', {
      method: 'POST',
      credentials: 'include'
    });
    const payload = response.ok ? ((await response.json()) as { logout_url: string }) : null;
    setSession(null);
    window.location.assign(payload?.logout_url ?? '/');
  };

  const metadataIsValid = useMemo(() => {
    return Boolean(metadata.job_name.trim() && metadata.description.trim());
  }, [metadata]);

  const metadataPayload = useMemo<UploadMetadata>(() => {
    const payload: UploadMetadata = {
      job_name: metadata.job_name,
      description: metadata.description
    };
    for (const key of ['camera_crew_names', 'location', 'date_of_shoot', 'job_number'] as const) {
      const value = metadata[key]?.trim();
      if (value) {
        payload[key] = value;
      }
    }
    return payload;
  }, [metadata]);

  const handleSelectedFiles = (selectedFiles: SelectedFile[]) => {
    if (!metadataIsValid) {
      addNotice('error', 'Job name and footage description are required.');
      return;
    }

    if (selectedFiles.length === 0) {
      addNotice('info', 'No supported files were found.');
      return;
    }

    const items = selectedFiles.map<UploadItem>(({ file, relativePath }) => ({
      id: crypto.randomUUID(),
      file,
      relativePath,
      metadata: metadataPayload,
      status: 'queued',
      progress: 0,
      uploadedBytes: 0,
      speedBps: 0,
      etaSeconds: null
    }));

    setQueue((current) => [...items, ...current]);
    void startUploadBatch(items);
  };

  const startUploadBatch = async (items: UploadItem[]) => {
    const csrfToken = session?.csrf_token;
    if (!csrfToken) {
      addNotice('error', 'Your session is not ready. Please sign in again.');
      return;
    }

    const results = await Promise.all(items.map((item) => startUpload(item, csrfToken)));
    if (!results.every(Boolean)) {
      addNotice('info', 'Upload notification was not sent because one or more files did not finish successfully.');
      return;
    }

    try {
      await notifyUploadBatchComplete(items, csrfToken);
      addNotice('success', 'Upload notification sent.');
    } catch (error) {
      addNotice('error', errorMessage(error));
    }
  };

  const startUpload = async (item: UploadItem, csrfToken: string): Promise<boolean> => {
    const controller = new AbortController();
    const startedAt = performance.now();
    updateItem(item.id, { status: 'preparing', controller });

    try {
      const uploadPlan = await createUploadPlan(item, csrfToken);
      updateItem(item.id, {
        status: 'uploading',
        objectKey: uploadPlan.object_key
      });

      let parts: CompletedUploadPart[] = [];
      if (uploadPlan.upload_type === 'single') {
        if (!uploadPlan.upload_url) {
          throw new Error('Upload URL was not returned.');
        }
        await uploadSingleFile(item, uploadPlan, controller.signal, startedAt);
      } else {
        parts = await uploadMultipartFile(item, uploadPlan, controller.signal, startedAt);
      }

      updateItem(item.id, { status: 'finalizing', progress: 100, uploadedBytes: item.file.size });
      await completeUpload(item, uploadPlan, parts, csrfToken);
      updateItem(item.id, {
        status: 'complete',
        progress: 100,
        uploadedBytes: item.file.size,
        etaSeconds: 0,
        controller: undefined
      });
      addNotice('success', `${uploadDisplayName(item)} uploaded successfully.`);
      return true;
    } catch (error) {
      const cancelled = controller.signal.aborted;
      updateItem(item.id, {
        status: cancelled ? 'cancelled' : 'failed',
        error: cancelled ? 'Upload cancelled.' : errorMessage(error),
        controller: undefined
      });
      addNotice(cancelled ? 'info' : 'error', cancelled ? `${uploadDisplayName(item)} was cancelled.` : errorMessage(error));
      return false;
    }
  };

  const createUploadPlan = async (item: UploadItem, csrfToken: string): Promise<UploadUrlResponse> => {
    const response = await fetch('/upload-url', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        file_name: item.file.name,
        relative_path: item.relativePath,
        content_type: item.file.type || 'application/octet-stream',
        size_bytes: item.file.size,
        metadata: item.metadata
      })
    });

    if (!response.ok) {
      throw new Error(await responseErrorMessage(response));
    }
    return response.json();
  };

  const uploadSingleFile = (
    item: UploadItem,
    uploadPlan: UploadUrlResponse,
    signal: AbortSignal,
    startedAt: number
  ) => {
    return putWithProgress({
      url: uploadPlan.upload_url as string,
      body: item.file,
      headers: uploadPlan.headers,
      signal,
      onProgress: (loaded) => updateProgress(item.id, loaded, item.file.size, startedAt)
    });
  };

  const uploadMultipartFile = async (
    item: UploadItem,
    uploadPlan: UploadUrlResponse,
    signal: AbortSignal,
    startedAt: number
  ): Promise<CompletedUploadPart[]> => {
    if (!uploadPlan.part_size_bytes || !uploadPlan.upload_id) {
      throw new Error('Multipart upload details were not returned.');
    }

    const partProgress = new Map<number, number>();
    const completedParts: CompletedUploadPart[] = [];
    let nextPartIndex = 0;
    const workerCount = Math.min(3, uploadPlan.parts.length);

    const updateAggregateProgress = () => {
      const uploadedBytes = Array.from(partProgress.values()).reduce((total, bytes) => total + bytes, 0);
      updateProgress(item.id, uploadedBytes, item.file.size, startedAt);
    };

    const uploadPart = async (part: UploadPartUrl) => {
      const start = (part.part_number - 1) * (uploadPlan.part_size_bytes as number);
      const end = Math.min(start + (uploadPlan.part_size_bytes as number), item.file.size);
      const chunk = item.file.slice(start, end);
      const etag = await putWithProgress({
        url: part.url,
        body: chunk,
        headers: {},
        signal,
        onProgress: (loaded) => {
          partProgress.set(part.part_number, loaded);
          updateAggregateProgress();
        }
      });
      completedParts[part.part_number - 1] = {
        part_number: part.part_number,
        etag
      };
    };

    const worker = async () => {
      while (nextPartIndex < uploadPlan.parts.length) {
        if (signal.aborted) {
          throw new Error('Upload cancelled.');
        }
        const part = uploadPlan.parts[nextPartIndex];
        nextPartIndex += 1;
        await uploadPart(part);
      }
    };

    await Promise.all(Array.from({ length: workerCount }, worker));
    return completedParts;
  };

  const completeUpload = async (
    item: UploadItem,
    uploadPlan: UploadUrlResponse,
    parts: CompletedUploadPart[],
    csrfToken: string
  ) => {
    const response = await fetch('/upload-complete', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        object_key: uploadPlan.object_key,
        file_name: item.file.name,
        relative_path: item.relativePath,
        content_type: item.file.type || 'application/octet-stream',
        size_bytes: item.file.size,
        metadata: item.metadata,
        upload_id: uploadPlan.upload_id,
        parts
      })
    });

    if (!response.ok) {
      throw new Error(await responseErrorMessage(response));
    }
  };

  const notifyUploadBatchComplete = async (items: UploadItem[], csrfToken: string) => {
    const firstItem = items[0];
    if (!firstItem) {
      return;
    }

    const payload: UploadBatchCompleteRequest = {
      metadata: firstItem.metadata,
      file_count: items.length,
      total_size_bytes: items.reduce((total, item) => total + item.file.size, 0)
    };
    const response = await fetch('/upload-batch-complete', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(await responseErrorMessage(response));
    }
  };

  const updateProgress = (id: string, uploadedBytes: number, totalBytes: number, startedAt: number) => {
    const elapsedSeconds = Math.max((performance.now() - startedAt) / 1000, 0.1);
    const speedBps = uploadedBytes / elapsedSeconds;
    const remainingBytes = Math.max(totalBytes - uploadedBytes, 0);
    updateItem(id, {
      uploadedBytes,
      progress: Math.min(100, Math.round((uploadedBytes / totalBytes) * 1000) / 10),
      speedBps,
      etaSeconds: speedBps > 0 ? remainingBytes / speedBps : null
    });
  };

  const updateItem = (id: string, patch: Partial<UploadItem>) => {
    setQueue((items) => items.map((existing) => (existing.id === id ? { ...existing, ...patch } : existing)));
  };

  const cancelUpload = (item: UploadItem) => {
    item.controller?.abort();
    updateItem(item.id, {
      status: 'cancelled',
      error: 'Upload cancelled.',
      controller: undefined
    });
  };

  const removeItem = (id: string) => {
    setQueue((items) => items.filter((item) => item.id !== id));
  };

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.currentTarget.classList.remove('is-dragging');
    const { dataTransfer } = event;
    void collectDroppedFiles(dataTransfer)
      .then(handleSelectedFiles)
      .catch(() => addNotice('error', 'Dropped folders could not be read by this browser.'));
  };

  const onFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      handleSelectedFiles(selectedFilesFromFileList(event.target.files));
      event.target.value = '';
    }
  };

  if (loadingSession) {
    return <LoadingScreen />;
  }

  if (!session) {
    return <LoginScreen onLogin={() => void startLogin()} notices={notices} />;
  }

  if (!session.authorized || isUnauthorizedRoute) {
    return <UnauthorizedScreen session={session} onLogout={() => void logout()} />;
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand-block">
          <img className="brand-logo" src={nascarLogo} alt="NASCAR Productions" />
          <div className="brand-copy">
            <p className="eyebrow">Secure uploads</p>
            <h1>{APP_TITLE}</h1>
          </div>
        </div>
        <div className="user-block">
          <div className="user-copy">
            <strong>{session.user.name ?? session.user.email ?? 'Signed in'}</strong>
            <span>{session.user.email}</span>
          </div>
          <button className="icon-button" type="button" onClick={() => void logout()} title="Log out">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <section className="upload-grid">
        <form className="metadata-panel" onSubmit={(event) => event.preventDefault()}>
          <div className="section-heading">
            <h2>Job metadata</h2>
          </div>
          <label>
            Job Name
            <input
              value={metadata.job_name}
              onChange={(event) => setMetadata({ ...metadata, job_name: event.target.value })}
              maxLength={120}
            />
          </label>
          <label>
            Footage Description
            <textarea
              value={metadata.description}
              onChange={(event) => setMetadata({ ...metadata, description: event.target.value })}
              maxLength={1000}
              rows={5}
            />
          </label>
          <label>
            Camera Crew Names
            <input
              value={metadata.camera_crew_names ?? ''}
              onChange={(event) => setMetadata({ ...metadata, camera_crew_names: event.target.value })}
              maxLength={500}
            />
          </label>
          <label>
            Location
            <input
              value={metadata.location ?? ''}
              onChange={(event) => setMetadata({ ...metadata, location: event.target.value })}
              maxLength={500}
            />
          </label>
          <label>
            Date of Shoot
            <input
              type="date"
              value={metadata.date_of_shoot ?? ''}
              onChange={(event) => setMetadata({ ...metadata, date_of_shoot: event.target.value })}
            />
          </label>
          <label>
            Job Number
            <input
              value={metadata.job_number ?? ''}
              onChange={(event) => setMetadata({ ...metadata, job_number: event.target.value })}
              maxLength={120}
            />
          </label>
        </form>

        <section className="upload-panel">
          <div className="section-heading">
            <h2>Uploads</h2>
            <div className="upload-actions">
              <button
                className="primary-button"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!metadataIsValid}
              >
                <FileUp size={18} />
                Browse Files
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={() => folderInputRef.current?.click()}
                disabled={!metadataIsValid}
              >
                <FolderOpen size={18} />
                Browse Folder
              </button>
            </div>
          </div>
          <input ref={fileInputRef} className="hidden-input" type="file" multiple onChange={onFileInput} />
          <input ref={setFolderInputRef} className="hidden-input" type="file" multiple onChange={onFileInput} />
          <label
            className="drop-zone"
            onDragOver={(event) => {
              event.preventDefault();
              event.currentTarget.classList.add('is-dragging');
            }}
            onDragLeave={(event) => event.currentTarget.classList.remove('is-dragging')}
            onDrop={onDrop}
          >
            <input className="hidden-input" type="file" multiple onChange={onFileInput} />
            <UploadCloud size={42} />
            <span>Drop media files or folders here</span>
          </label>
          <UploadQueue items={queue} onCancel={cancelUpload} onRemove={removeItem} />
        </section>
      </section>
      <NoticeStack notices={notices} />
    </main>
  );
}

function LoginScreen({ onLogin, notices }: { onLogin: () => void; notices: Notice[] }) {
  return (
    <main className="login-shell">
      <section className="login-panel">
        <img className="brand-logo large" src={nascarLogo} alt="NASCAR Productions" />
        <p className="eyebrow">Secure media uploads</p>
        <h1>{APP_TITLE}</h1>
        <button className="primary-button wide" type="button" onClick={onLogin}>
          <ShieldAlert size={18} />
          Sign in with Auth0
        </button>
      </section>
      <NoticeStack notices={notices} />
    </main>
  );
}

function UnauthorizedScreen({ session, onLogout }: { session: MeResponse; onLogout: () => void }) {
  return (
    <main className="login-shell">
      <section className="login-panel permission">
        <ShieldAlert size={44} />
        <p className="eyebrow">Access restricted</p>
        <h1>You do not have permission to access this application.</h1>
        <p className="muted">
          Signed in as {session.user.email ?? session.user.name ?? session.user.sub}. Required role:{' '}
          {session.required_role}
        </p>
        <button className="secondary-button wide" type="button" onClick={onLogout}>
          <LogOut size={18} />
          Log out
        </button>
      </section>
    </main>
  );
}

function LoadingScreen() {
  return (
    <main className="login-shell">
      <Loader2 className="spin" size={34} />
    </main>
  );
}

function UploadQueue({
  items,
  onCancel,
  onRemove
}: {
  items: UploadItem[];
  onCancel: (item: UploadItem) => void;
  onRemove: (id: string) => void;
}) {
  if (items.length === 0) {
    return (
      <div className="empty-queue">
        <FileUp size={24} />
        <span>No files queued</span>
      </div>
    );
  }

  return (
    <div className="queue-list">
      {items.map((item) => (
        <article className="queue-item" key={item.id}>
          <div className="file-icon">
            <FileUp size={20} />
          </div>
          <div className="file-main">
            <div className="file-line">
              <strong>{item.file.name}</strong>
              <span>{formatBytes(item.file.size)}</span>
            </div>
            {item.relativePath && <div className="file-path">{item.relativePath}</div>}
            <div className="progress-track">
              <div className={`progress-fill ${item.status}`} style={{ width: `${item.progress}%` }} />
            </div>
            <div className="status-line">
              <StatusBadge status={item.status} />
              <span>{item.progress.toFixed(1)}%</span>
              <span>{formatBytes(item.uploadedBytes)} uploaded</span>
              {item.speedBps > 0 && <span>{formatBytes(item.speedBps)}/s</span>}
              {item.etaSeconds !== null && item.status === 'uploading' && (
                <span>
                  <Clock size={14} />
                  {formatDuration(item.etaSeconds)}
                </span>
              )}
              {item.error && <span className="error-text">{item.error}</span>}
            </div>
          </div>
          <div className="queue-actions">
            {['preparing', 'uploading', 'finalizing'].includes(item.status) ? (
              <button className="icon-button danger" type="button" onClick={() => onCancel(item)} title="Cancel upload">
                <XCircle size={18} />
              </button>
            ) : (
              <button className="icon-button" type="button" onClick={() => onRemove(item.id)} title="Remove from queue">
                <X size={18} />
              </button>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: UploadStatus }) {
  const icon = status === 'complete' ? <CheckCircle2 size={14} /> : status === 'failed' ? <AlertCircle size={14} /> : null;
  return <span className={`status-badge ${status}`}>{icon}{status}</span>;
}

function NoticeStack({ notices }: { notices: Notice[] }) {
  return (
    <div className="notice-stack">
      {notices.map((notice) => (
        <div className={`notice ${notice.tone}`} key={notice.id}>
          {notice.tone === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{notice.message}</span>
        </div>
      ))}
    </div>
  );
}

function selectedFilesFromFileList(files: FileList | File[]): SelectedFile[] {
  return Array.from(files).map((file) => {
    const relativePath = normalizeRelativePath((file as FileWithRelativePath).webkitRelativePath ?? '');
    return {
      file,
      relativePath: relativePath && relativePath !== file.name ? relativePath : undefined
    };
  });
}

async function collectDroppedFiles(dataTransfer: DataTransfer): Promise<SelectedFile[]> {
  const entries = Array.from(dataTransfer.items)
    .filter((item) => item.kind === 'file')
    .map((item) => (item as DataTransferItemWithEntry).webkitGetAsEntry?.())
    .filter((entry): entry is BrowserFileSystemEntry => Boolean(entry));

  if (entries.length === 0) {
    return selectedFilesFromFileList(dataTransfer.files);
  }

  const nestedFiles = await Promise.all(entries.map((entry) => selectedFilesFromEntry(entry)));
  return nestedFiles.flat();
}

async function selectedFilesFromEntry(entry: BrowserFileSystemEntry, parentPath = ''): Promise<SelectedFile[]> {
  const entryPath = normalizeRelativePath(parentPath ? `${parentPath}/${entry.name}` : entry.name);

  if (entry.isFile) {
    const file = await fileFromEntry(entry as BrowserFileSystemFileEntry);
    return [
      {
        file,
        relativePath: entryPath && entryPath !== file.name ? entryPath : undefined
      }
    ];
  }

  if (!entry.isDirectory) {
    return [];
  }

  const childEntries = await readDirectoryEntries(entry as BrowserFileSystemDirectoryEntry);
  const nestedFiles = await Promise.all(childEntries.map((childEntry) => selectedFilesFromEntry(childEntry, entryPath)));
  return nestedFiles.flat();
}

function fileFromEntry(entry: BrowserFileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

async function readDirectoryEntries(entry: BrowserFileSystemDirectoryEntry): Promise<BrowserFileSystemEntry[]> {
  const reader = entry.createReader();
  const entries: BrowserFileSystemEntry[] = [];

  while (true) {
    const batch = await new Promise<BrowserFileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) {
      return entries;
    }
    entries.push(...batch);
  }
}

function normalizeRelativePath(path: string): string {
  return path
    .replaceAll('\\', '/')
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part && part !== '.' && part !== '..')
    .join('/');
}

function uploadDisplayName(item: UploadItem): string {
  return item.relativePath ?? item.file.name;
}

function putWithProgress({
  url,
  body,
  headers,
  signal,
  onProgress
}: {
  url: string;
  body: Blob;
  headers: Record<string, string>;
  signal: AbortSignal;
  onProgress: (loaded: number) => void;
}): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const abort = () => {
      xhr.abort();
      reject(new Error('Upload cancelled.'));
    };

    signal.addEventListener('abort', abort, { once: true });
    xhr.open('PUT', url);
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded);
      }
    };
    xhr.onload = () => {
      signal.removeEventListener('abort', abort);
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(body.size);
        resolve(xhr.getResponseHeader('ETag')?.replaceAll('"', '') ?? '');
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}.`));
      }
    };
    xhr.onerror = () => {
      signal.removeEventListener('abort', abort);
      reject(new Error('Network error while uploading to S3.'));
    };
    xhr.onabort = () => {
      signal.removeEventListener('abort', abort);
      reject(new Error('Upload cancelled.'));
    };
    xhr.send(body);
  });
}

async function responseErrorMessage(response: Response): Promise<string> {
  try {
    const payload = await response.json();
    return payload?.error?.message ?? `Request failed with status ${response.status}.`;
  } catch {
    return `Request failed with status ${response.status}.`;
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unexpected upload error.';
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value >= 10 || exponent === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[exponent]}`;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) {
    return '';
  }
  const rounded = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  if (minutes <= 0) {
    return `${remainder}s`;
  }
  return `${minutes}m ${remainder}s`;
}

createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
